import { Component, Input, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  getAllAvailableExportSettings,
  getZipSettingsForSession,
  setNeedToSaveSettings,
} from '../../../../redux/mydoc.action';
import { SubscriptionComponent } from '../../../../shared/subscription/subscription.component';
import {
  availableConfiguration,
  defaultConfiguration,
  documentSession,
  savedConfiguration,
  storedSession,
} from '../../../../redux/mydoc.selector';
import { ISession } from '../../../../models/session/session.models';
import { getValue, isDefined } from '../../../../shared/utility/verify.lib';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { combineLatest, takeUntil } from 'rxjs';
import { IExportConfiguration } from '../../../../models/export/export-configuration.model';
import { ExportStructureHelperService } from '../../services/export-structure-helper/export-structure-helper.service';
import { IExportStructure } from '../../../../models/export/export-structure.model';
import { IDocumentSession } from '../../../../models/session/document-session.model';

@Component({
  selector: 'app-export-structure',
  templateUrl: './export-structure.component.html',
  styleUrl: './export-structure.component.scss',
})
export class ExportStructureComponent
  extends SubscriptionComponent
  implements OnInit
{
  @Input() public activeLanguageId: string = 'EN';

  public isExportStruktureVisible: boolean = false;
  public rememberSettings: boolean = false;

  protected availableConfiguration$ = this.store.select(availableConfiguration);
  private defaultConfiguration$ = this.store.select(defaultConfiguration);
  private savedConfiguration$ = this.store.select(savedConfiguration);
  private documentSession$ = this.store.select(documentSession);
  private storedSession$ = this.store.select(storedSession);
  private userChangedRememberSettings: boolean = false;

  public constructor(
    private store: Store,
    private exportStructureHelper: ExportStructureHelperService
  ) {
    super();
  }

  public ngOnInit(): void {
    const currentSession: ISession = this.getLatestValueFromSubscription(
      this.storedSession$
    );
    this.checkIfInputsChanged();

    this.store.dispatch(
      getAllAvailableExportSettings({
        sessionId: getValue(() => currentSession.id),
        orderCode: getValue(() => currentSession.orderNumber),
      })
    );
    this.store.dispatch(
      getZipSettingsForSession({
        sessionId: getValue(() => currentSession.id),
        orderCode: getValue(() => currentSession.orderNumber),
      })
    );
  }

  public onToggleChanged(toggleState: boolean): void {
    this.isExportStruktureVisible = toggleState;
  }

  public onRememberSettings(event: MatCheckboxChange): void {
    this.rememberSettings = !this.rememberSettings;
    this.store.dispatch(setNeedToSaveSettings({ saveSettings: event.checked }));
    if (!this.rememberSettings) {
      this.userChangedRememberSettings = true;
    }
  }

  private checkIfInputsChanged(): void {
    combineLatest([
      this.defaultConfiguration$,
      this.savedConfiguration$,
      this.documentSession$,
    ])
      .pipe(takeUntil(this.completeAll$))
      .subscribe(
        ([defaultConfig, savedConfig, documentSession]: [
          IExportConfiguration,
          IExportConfiguration,
          IDocumentSession
        ]) => {
          if (
            isDefined(() => documentSession) &&
            !this.userChangedRememberSettings
          ) {
            this.rememberSettings =
              this.rememberSettings ||
              this.exportStructureHelper.didShortNamesChange(
                this.configWithoutFilteredEntries(defaultConfig),
                this.configWithoutFilteredEntries(savedConfig),
                documentSession,
                true,
                true
              );
            this.store.dispatch(
              setNeedToSaveSettings({ saveSettings: this.rememberSettings })
            );
          }
        }
      );
  }

  private configWithoutFilteredEntries(
    config: IExportConfiguration
  ): IExportConfiguration {
    const availableConfiguration: IExportConfiguration =
      this.getLatestValueFromSubscription(this.availableConfiguration$);
    const documentSession: IDocumentSession =
      this.getLatestValueFromSubscription(this.documentSession$);
    const availableFolderShortNames: string[] =
      this.exportStructureHelper.getShortNames(
        getValue(() => availableConfiguration.folderSetting),
        documentSession
      );
    const availableFileShortNames: string[] =
      this.exportStructureHelper.getShortNames(
        getValue(() => availableConfiguration.fileSetting),
        documentSession
      );
    const filteredFolderSettings: IExportStructure[] =
      this.exportStructureHelper.filterAvailableStructure(
        getValue(() => config.folderSetting, []),
        availableFolderShortNames,
        documentSession
      );
    const filteredFileSettings: IExportStructure[] =
      this.exportStructureHelper.filterAvailableStructure(
        getValue(() => config.fileSetting, []),
        availableFileShortNames,
        documentSession
      );

    return {
      ...config,
      fileSetting: filteredFileSettings,
      folderSetting: filteredFolderSettings,
    };
  }
}
